import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react';
import type { TemplateConfig, TemplateId } from '../types';

// ─── State ───────────────────────────────────────────────────

interface TemplateState {
  templates: TemplateConfig[];        // all loaded template configs
  activeTemplateId: TemplateId | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: TemplateState = {
  templates: [],
  activeTemplateId: null,
  isLoading: false,
  error: null,
};

// ─── Actions ─────────────────────────────────────────────────

type TemplateAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: TemplateConfig[] }
  | { type: 'LOAD_ERROR'; payload: string }
  | { type: 'SELECT_TEMPLATE'; payload: TemplateId };

function templateReducer(state: TemplateState, action: TemplateAction): TemplateState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, isLoading: true, error: null };

    case 'LOAD_SUCCESS':
      return {
        ...state,
        isLoading: false,
        templates: action.payload,
        // auto-select first template on initial load if none selected
        activeTemplateId:
          state.activeTemplateId ?? action.payload[0]?.metadata.id ?? null,
      };

    case 'LOAD_ERROR':
      return { ...state, isLoading: false, error: action.payload };

    case 'SELECT_TEMPLATE':
      return { ...state, activeTemplateId: action.payload };

    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────

interface TemplateContextValue {
  state: TemplateState;
  activeTemplate: TemplateConfig | null;
  selectTemplate: (id: TemplateId) => void;
  loadTemplates: (configs: TemplateConfig[]) => void;
  setError: (message: string) => void;
}

const TemplateContext = createContext<TemplateContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────

interface TemplateProviderProps {
  children: ReactNode;
}

export function TemplateProvider({ children }: TemplateProviderProps) {
  const [state, dispatch] = useReducer(templateReducer, initialState);

  const activeTemplate =
    state.templates.find((t) => t.metadata.id === state.activeTemplateId) ?? null;

  const selectTemplate = useCallback((id: TemplateId) => {
    dispatch({ type: 'SELECT_TEMPLATE', payload: id });
  }, []);

  const loadTemplates = useCallback((configs: TemplateConfig[]) => {
    dispatch({ type: 'LOAD_SUCCESS', payload: configs });
  }, []);

  const setError = useCallback((message: string) => {
    dispatch({ type: 'LOAD_ERROR', payload: message });
  }, []);

  return (
    <TemplateContext.Provider
      value={{ state, activeTemplate, selectTemplate, loadTemplates, setError }}
    >
      {children}
    </TemplateContext.Provider>
  );
}

// ─── Consumer hook (throws if used outside provider) ─────────

export function useTemplateContext(): TemplateContextValue {
  const ctx = useContext(TemplateContext);
  if (!ctx) {
    throw new Error('useTemplateContext must be used inside <TemplateProvider>');
  }
  return ctx;
}
